import { useEffect, useState } from 'react';
import { Megaphone, Plus, Pencil, Send } from 'lucide-react';
import {
  Card,
  Button,
  Input,
  FormField,
  StatusBadge,
  Modal,
  ModalBody,
  ModalFooter,
  NotificationCard,
  UniversalLoadingState,
  useToast,
} from '@afrilink/ui';
import {
  announcementService,
  type Announcement,
  type AnnouncementTone,
  type AnnouncementAudience,
} from '@/domains/announcements/services/announcementService';

/**
 * AnnouncementsPage — admin CRUD for the portal banner (W5 / GA-D3,
 * 2026-08-11). Follows the EmailTemplatesPage hand-rolled pattern. Writes
 * are full-staff at the RLS layer; the email blast is dry-run-locked until
 * the deliverability P0 clears (W5-D1) — the send button always runs a dry
 * run first and real sends need the explicit confirm.
 */

interface FormState {
  id: string | null;
  title: string;
  body: string;
  tone: AnnouncementTone;
  audience: AnnouncementAudience;
  ends_at: string; // datetime-local value or ''
  is_active: boolean;
}

const EMPTY_FORM: FormState = {
  id: null,
  title: '',
  body: '',
  tone: 'info',
  audience: 'customers',
  ends_at: '',
  is_active: true,
};

export default function AnnouncementsPage() {
  const { toast } = useToast();
  const [rows, setRows] = useState<Announcement[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);
  const [blasting, setBlasting] = useState<string | null>(null);
  const [blastPreview, setBlastPreview] = useState<{ announcement: Announcement; targetCount: number } | null>(null);
  /** The deliberate friction. See the dialog for why a checkbox and not just a button. */
  const [blastAck, setBlastAck] = useState(false);

  function reload() {
    announcementService
      .listAll()
      .then(setRows)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }
  useEffect(reload, []);

  async function save() {
    if (!form || !form.title.trim() || !form.body.trim()) return;
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        body: form.body.trim(),
        tone: form.tone,
        audience: form.audience,
        ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : null,
        is_active: form.is_active,
      };
      if (form.id) await announcementService.update(form.id, payload);
      else await announcementService.create(payload);
      toast({ tone: 'success', title: form.id ? 'Announcement updated' : 'Announcement published', body: '' });
      setForm(null);
      reload();
    } catch (e) {
      toast({ tone: 'danger', title: 'Could not save', body: e instanceof Error ? e.message : String(e) });
    } finally {
      setSaving(false);
    }
  }

  /**
   * Step 1 of 2 — dry run, then open the confirm dialog. (F-27)
   *
   * This is the widest outward-facing action in the product: it emails every
   * active customer. It was gated by `window.confirm` — unstyled browser text
   * with `\n\n` in it, no way to require anything of the person clicking, and
   * suppressible by the browser after one use. Meanwhile the SMALLER action
   * (overdue payment reminders on the Invoices page) already had the correct
   * pattern: dry run, real Modal, itemised preview, explicit send. The stronger
   * control was on the weaker action.
   *
   * The dry run still runs first and its count is what the dialog reports, so
   * the audience size is measured rather than estimated.
   */
  async function previewBlast(a: Announcement) {
    setBlasting(a.id);
    try {
      const dry = await announcementService.sendBlast(a.id, true);
      setBlastPreview({ announcement: a, targetCount: dry.target_count });
      setBlastAck(false);
    } catch (e) {
      toast({ tone: 'danger', title: 'Dry run failed', body: e instanceof Error ? e.message : String(e) });
    } finally {
      setBlasting(null);
    }
  }

  /** Step 2 — the real send, only reachable from the dialog. */
  async function confirmBlast() {
    if (!blastPreview) return;
    const a = blastPreview.announcement;
    setBlasting(a.id);
    try {
      const real = await announcementService.sendBlast(a.id, false);
      toast({
        tone: 'success',
        title: 'Announcement emailed',
        body: `${real.enqueued} queued, ${real.skipped} already sent previously.`,
      });
      setBlastPreview(null);
      reload();
    } catch (e) {
      toast({ tone: 'danger', title: 'Blast failed', body: e instanceof Error ? e.message : String(e) });
    } finally {
      setBlasting(null);
    }
  }

  const now = Date.now();

  return (
    <div className="space-y-4">
      <Card interactive={false} className="p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-start gap-3">
            <Megaphone className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
            <div>
              <h2 className="text-sm font-semibold text-navy-primary">Announcements</h2>
              <p className="mt-1 text-xs text-foreground-soft">
                Banner shown to signed-in users (maintenance, incidents, product news).
                Expired or deactivated rows disappear immediately — no redeploy needed.
              </p>
            </div>
          </div>
          <Button size="sm" onClick={() => setForm(EMPTY_FORM)}>
            <Plus className="h-4 w-4" /> New announcement
          </Button>
        </div>
      </Card>

      {error && <NotificationCard tone="danger" title="Could not load announcements" body={error} />}

      <Card interactive={false}>
        {!rows ? (
          <UniversalLoadingState variant="rows" count={3} />
        ) : rows.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground">
            No announcements yet. The banner stays hidden until one is published.
          </p>
        ) : (
          <ol className="divide-y divide-border">
            {rows.map((a) => {
              const expired = a.endsAt !== null && new Date(a.endsAt).getTime() <= now;
              const live = a.isActive && !expired && new Date(a.startsAt).getTime() <= now;
              return (
                <li key={a.id} className="flex flex-wrap items-center gap-3 p-4">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground">{a.title}</p>
                    <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{a.body}</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {a.audience} · from {new Date(a.startsAt).toLocaleString('en-ZA')}
                      {a.endsAt ? ` until ${new Date(a.endsAt).toLocaleString('en-ZA')}` : ' — no end date'}
                      {a.emailSentAt ? ` · emailed ${new Date(a.emailSentAt).toLocaleDateString('en-ZA')}` : ''}
                    </p>
                  </div>
                  <StatusBadge tone={live ? 'active' : 'draft'} dot>
                    {live ? 'Live' : expired ? 'Expired' : a.isActive ? 'Scheduled' : 'Off'}
                  </StatusBadge>
                  <StatusBadge tone={a.tone === 'danger' ? 'overdue' : a.tone === 'warning' ? 'pending' : 'info'}>
                    {a.tone}
                  </StatusBadge>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setForm({
                          id: a.id,
                          title: a.title,
                          body: a.body,
                          tone: a.tone,
                          audience: a.audience,
                          ends_at: a.endsAt ? a.endsAt.slice(0, 16) : '',
                          is_active: a.isActive,
                        })
                      }
                    >
                      <Pencil className="h-4 w-4" /> Edit
                    </Button>
                    {a.audience !== 'staff' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => previewBlast(a)}
                        loading={blasting === a.id}
                        disabled={blasting !== null}
                      >
                        <Send className="h-4 w-4" /> Email customers
                      </Button>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </Card>

      <Modal
        open={form !== null}
        onOpenChange={(v) => !v && setForm(null)}
        title={form?.id ? 'Edit announcement' : 'New announcement'}
        description="Shown as a banner to signed-in users the moment it saves (while active and inside its window)."
      >
        {form && (
          <>
            <ModalBody>
              <div className="space-y-4">
                <FormField label="Title" htmlFor="ann-title" required>
                  <Input
                    id="ann-title"
                    value={form.title}
                    maxLength={120}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                  />
                </FormField>
                <FormField label="Message" htmlFor="ann-body" required>
                  <textarea
                    id="ann-body"
                    value={form.body}
                    maxLength={1000}
                    rows={4}
                    onChange={(e) => setForm({ ...form, body: e.target.value })}
                    className="w-full rounded border border-input bg-card px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  />
                </FormField>
                <div className="grid gap-3 sm:grid-cols-2">
                  <FormField label="Tone" htmlFor="ann-tone">
                    <select
                      id="ann-tone"
                      value={form.tone}
                      onChange={(e) => setForm({ ...form, tone: e.target.value as AnnouncementTone })}
                      className="h-10 w-full rounded border border-input bg-card px-3 text-sm"
                    >
                      <option value="info">Info</option>
                      <option value="warning">Warning</option>
                      <option value="danger">Incident</option>
                    </select>
                  </FormField>
                  <FormField label="Audience" htmlFor="ann-audience">
                    <select
                      id="ann-audience"
                      value={form.audience}
                      onChange={(e) => setForm({ ...form, audience: e.target.value as AnnouncementAudience })}
                      className="h-10 w-full rounded border border-input bg-card px-3 text-sm"
                    >
                      <option value="customers">Customers</option>
                      <option value="staff">Staff</option>
                      <option value="all">Everyone</option>
                    </select>
                  </FormField>
                </div>
                <FormField
                  label="Ends at (optional)"
                  htmlFor="ann-ends"
                  helper="Leave empty to keep it up until switched off."
                >
                  <Input
                    id="ann-ends"
                    type="datetime-local"
                    value={form.ends_at}
                    onChange={(e) => setForm({ ...form, ends_at: e.target.value })}
                  />
                </FormField>
                <label className="flex items-center gap-2 text-sm text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  />
                  Active
                </label>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant="outline" onClick={() => setForm(null)} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={save} disabled={saving || !form.title.trim() || !form.body.trim()} loading={saving}>
                {form.id ? 'Save changes' : 'Publish'}
              </Button>
            </ModalFooter>
          </>
        )}
      </Modal>

      {/* F-27. Replaces `window.confirm` for the widest outward-facing action
          in the product. Ported from the overdue-reminders flow on the Invoices
          page, which already did this correctly for a much smaller blast. */}
      <Modal
        open={blastPreview !== null}
        onOpenChange={(v) => !v && setBlastPreview(null)}
        title="Email this announcement to every active customer"
        description="This cannot be recalled once sent."
        size="wide"
      >
        {blastPreview && (
          <>
            <ModalBody>
              <p className="text-sm text-foreground">
                <strong>{blastPreview.targetCount}</strong> active customer
                {blastPreview.targetCount === 1 ? '' : 's'} will receive{' '}
                <strong>“{blastPreview.announcement.title}”</strong>.
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Counted by a real dry run just now, not estimated. Customers already
                emailed this announcement are skipped automatically.
              </p>

              <div className="mt-4 rounded border border-warning/30 bg-warning/10 p-3">
                <p className="text-sm font-semibold text-warning-dark">
                  Deliverability gate still open
                </p>
                <p className="mt-1 text-xs text-foreground-soft">
                  The tracker §6 hard gate (DMARC ramp + dedicated sending subdomain)
                  has not cleared. A bulk send before it does risks the domain’s
                  sending reputation, which affects invoices and password resets —
                  not just this announcement.
                </p>
              </div>

              {/* The checkbox is the point. A confirm dialog you can dismiss with
                  Enter is one keystroke from a mistake you cannot undo; requiring
                  a deliberate second act is the difference between confirming and
                  acknowledging. Same reasoning as the refund flow's
                  cancel_subscription_ack. */}
              <label className="mt-4 flex cursor-pointer items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={blastAck}
                  onChange={(e) => setBlastAck(e.target.checked)}
                  className="mt-0.5 size-4 shrink-0 accent-primary"
                />
                <span className="text-foreground-soft">
                  I understand this emails {blastPreview.targetCount} customer
                  {blastPreview.targetCount === 1 ? '' : 's'} immediately and cannot be
                  undone.
                </span>
              </label>
            </ModalBody>
            <ModalFooter>
              <Button
                variant="outline"
                onClick={() => setBlastPreview(null)}
                disabled={blasting !== null}
              >
                Cancel — nothing sent
              </Button>
              <Button
                onClick={confirmBlast}
                disabled={!blastAck || blasting !== null || blastPreview.targetCount === 0}
                loading={blasting !== null}
              >
                <Send className="h-4 w-4" />
                Send to {blastPreview.targetCount}
              </Button>
            </ModalFooter>
          </>
        )}
      </Modal>
    </div>
  );
}
